import React, { useEffect, useState, useRef } from 'react';
import { notification, Modal, Popover, Button } from 'antd';
import Table from 'components/Table';
import { useSelector } from 'react-redux';
import './assets/style.css';
import { useQuery } from 'react-query';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { PageHeader } from 'components/Atom/PageHeader';
import { ResetButton, FilterButton } from 'components/Atom/Button';
import { ReloadOutlined } from '@ant-design/icons';
import Filter from './assets/filter';
import SearchField from 'components/Atom/SearchField/index';
import { getXPayUsers } from '../../utils/copilot';
import CreateUser from './createUser';
import { handleStateByPassData, checkUserPermissions } from 'utils/services';
import { USER_ROLES } from 'utils/constant';
import { useNavigate, useLocation } from 'react-router-dom';
import StatusTag from 'components/Atom/StatusTag';
import { formatInTimeZone } from 'date-fns-tz';

export const User = () => {
  const store = useSelector((state) => state?.company?.store);
  const [userModalData, setUserModalData] = useState({});
  const [filterToggle, setFilterToggle] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [delayTimer, setDelayTimer] = useState();
  const [filteredData, setFilteredData] = useState([]);
  const [localData, setLocalData] = useState([]);
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpen = () => {
    setOpen(!open);
    setIsEdit(false);
  };

  const getRoleLabel = (roles) => {
    if (!roles || roles?.length === 0) return 'N/A';
    if (roles?.length === 1 && roles[0] === 'xpay') return 'ADMIN';
    if (roles?.includes('xpay') && roles?.length > 1) {
      const otherRole = roles?.find((r) => r !== 'xpay');
      return getSingleRoleLabel(otherRole);
    }
    return roles?.map(getSingleRoleLabel)?.join(', ');
  };

  const getSingleRoleLabel = (value) => {
    if (value === 'xpay_user') return 'READ ONLY';
    else if (value === 'xpay') return 'ADMIN';
    else if (value === 'xstak-copilot-admin') return 'USER MANAGEMENT';
    else if (value === 'xpay_refund') return 'REFUND';
    else if (value === 'xpay_developer') return 'DEVELOPER';
    else if (value === 'xpay_restricted_admin') return 'RESTRICTED ADMIN';
    else if (value === 'xpay_subscription_user') return 'SUBSCRIPTION USER';
    else if (value === 'xpay_subscription_module_only') return 'SUBSCRIPTION MODULE ONLY';
    else return value;
  };

  const columnsData = [
    {
      title: 'Name',
      key: 'name',
      render: (record) => (record?.firstName || record?.lastName ? `${record?.firstName ?? ''} ${record?.lastName ?? ''}`?.trim() : 'N/A'),
    },
    {
      title: 'Email',
      key: 'email',
      render: (record) => record?.email || 'N/A',
    },
    {
      title: 'Role',
      key: 'role',
      render: (record) => getRoleLabel(record?.roles),
    },
    {
      title: 'Time',
      key: 'createdTimestamp',
      render: (record) => {
        const timezone = store?.settings?.timezone || 'Asia/Karachi';
        return record?.createdTimestamp ? formatInTimeZone(record?.createdTimestamp, timezone, 'MMM d, yyyy h:mm:ss a') : 'N/A';
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (record) => <StatusTag tag={record?.requiredActions?.includes('UPDATE_PASSWORD') && record?.enabled ? 'Activation Required' : record?.enabled ? 'Active' : 'Inactive'} />,
    },
    ...(checkUserPermissions([USER_ROLES.XPAY_COPILOT_ADMIN])
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (record, index) => (
              <div key={index} className='flex items-center gap-2'>
                <div
                  className='table-action-btn flex items-center justify-center'
                  onClick={() => {
                    setUserModalData(record);
                    setOpen(true);
                    setIsEdit(true);
                  }}
                  style={{ cursor: 'pointer' }}>
                  Update
                </div>
              </div>
            ),
          },
        ]
      : []),
  ];

  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const response = await getXPayUsers(store?.user_id);
      const data = response?.data || [];
      setLocalData(data);
      setFilteredData(data);
      return data;
    } catch (err) {
      console.log(err);
      return [];
    }
  };
  const { refetch, isLoading, isFetching } = useQuery('users', fetchData, {
    enabled: false,
    manual: true,
  });
  const [queryParams, setQueryParams] = useState({
    offset: 0,
    limit: 20,
    search: '',
    currentPage: 1,
  });

  useEffect(() => {
    if (location?.state?.isRedirection && filteredData) {
      setSearch(location?.state?.searchValue);
      navigate(location.pathname, { replace: true });
      const data = handleStateByPassData(location?.state?.filtersData, true);
      if (!data) {
        refetch();
      }
      setQueryParams(data);
      setTimeout(() => {
        if (tableContainerRef?.current && location?.state?.id) {
          const row = document?.querySelector(`.custom-row-class.highlighted-row`);
          if (row) {
            row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 10);
      return;
    } else if (location?.state?.isRedirection) {
      navigate(location?.pathname, { replace: true });
    }
    refetch();
  }, [location.pathname]);

  const getSearch = async (e) => {
    if (e === '' || e == null) {
      notification?.error({ message: 'Please enter details for search', placement: 'bottomRight' });
    } else {
      handleLocalSearch(e);
    }
  };

  const handleLocalSearch = (searchValue) => {
    if (!searchValue) {
      applyFilters(queryParams?.filtersData, localData);
      return;
    }

    const searchKey = searchValue?.toLowerCase()?.trim();
    const searchResults = localData?.filter((user) => user?.email?.toLowerCase()?.includes(searchKey));

    applyFilters(queryParams?.filtersData, searchResults);
  };

  const applyFilters = (filters, dataToFilter = filteredData) => {
    let result = [...dataToFilter];

    if (filters?.user_status && filters?.user_status !== 'all') {
      result = result?.filter((user) =>
        filters?.user_status === 'activation_required'
          ? user?.requiredActions?.includes('UPDATE_PASSWORD') && user?.enabled
          : filters?.user_status === 'active'
          ? user?.enabled && !user?.requiredActions?.includes('UPDATE_PASSWORD')
          : !user?.enabled
      );
    }

    if (filters?.role && filters?.role !== 'all') {
      result = result?.filter((user) => Array.isArray(user?.roles) && user?.roles?.includes(filters?.role));
    }

    setFilteredData(result);
  };

  const handleSearchData = () => {
    if (delayTimer) {
      clearTimeout(delayTimer);
    }
    const newTimer = setTimeout(() => {
      handleRefresh();
    }, 1500);
    setDelayTimer(newTimer);
  };
  const handleRefresh = (isRefresh = true) => {
    if (isRefresh) {
      setSearch(null);
    }
    setFilteredData(localData);
    setIsFilterModalOpen(false);
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1, search: '', filtersData: {} }));
    refetch();
  };
  const handleCloseFilterModel = () => {
    setIsFilterModalOpen(!isFilterModalOpen);
  };
  const handleFiltersSearch = (data) => {
    setQueryParams((prev) => ({ ...prev, filtersData: data }));
    applyFilters(data, localData);
  };
  const filterToggler = () => {
    setFilterToggle(true);
  };
  const handleReset = () => {
    setQueryParams((prev) => ({ ...prev, offset: 0, currentPage: 1, filtersData: {} }));
    setIsFilterModalOpen(false);
    refetch();
  };

  const popOverContent = (
    <div className='popOver'>
      <div className='popover_header mt-2 mb-3 flex items-center gap-2'>
        <span style={{ fontWeight: '700', color: '#262626' }}>Filter</span>
        <div className='flex items-center text-[#000000] gap-2 cursor-pointer' onClick={() => handleReset()}>
          <ReloadOutlined style={{ fontSize: '16px' }} />
          <span style={{ color: '#000000', fontSize: '14px', lineHeight: '24px' }}>Reset All</span>
        </div>
      </div>
      <div className='intentFilters'>
        <Filter filterToggler={filterToggler} filtersData={queryParams?.filtersData} onSearch={handleFiltersSearch} handleClose={handleCloseFilterModel} />
      </div>
    </div>
  );
  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Users',
      },
    ],
  };
  const handleSearch = (e) => {
    if (delayTimer && e !== '' && e !== null) {
      clearTimeout(delayTimer);
    }
    setSearch(e?.target?.value);
  };
  return (
    <div style={{ padding: '0px', width: '100%' }}>
      <div className='header-container'>
        <Breadcrumbs {...breadcrumbProps} />
        <div className='headerPage'>
          <div className='mb-0'>
            <PageHeader label='Users' button={<ResetButton onClick={isFetching ? '' : handleRefresh} loading={isFetching} />} />
          </div>

          {checkUserPermissions([USER_ROLES.XPAY_COPILOT_ADMIN]) && (
            <div className='flex txn-btn'>
              <div>
                <Button
                  className='primaryBtn mr-0 rounded-lg create_button'
                  onClick={() => {
                    setOpen(true);
                  }}>
                  <span className='text-base'>Create User</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='actionPanel'>
        <div className='headerContainer' style={{ width: '100%' }}>
          <Popover
            className={isFilterModalOpen ? 'filter-active filterPopover' : filterToggle ? 'text-base activeFilterToggler filterPopover' : 'text-base filterPopover'}
            trigger='click'
            placement='bottomLeft'
            content={popOverContent}
            open={isFilterModalOpen}
            destroyTooltipOnHide
            onOpenChange={() => {
              handleCloseFilterModel();
            }}>
            <FilterButton onClick={() => setIsFilterModalOpen(true)}>
              <div className='text-base'>Filter</div>
            </FilterButton>
          </Popover>
          <div className='searchInput' style={{ width: '100%' }}>
            <SearchField
              handleOnChange={handleSearch}
              value={search}
              handlePressEnter={getSearch}
              onReset={handleSearchData}
              placeholder={'Search by Email'}
              loading={!!(isFetching && search)}
              dropdown={{ show: false }}
              enableSearchBtn={true}
            />
          </div>
        </div>
      </div>
      <br />
      <div className='overflow-x-auto custom-table' ref={tableContainerRef}>
        <Table loading={isLoading || isFetching} dataSource={filteredData} columns={columnsData} scroll={{ x: 'auto' }} totalLenght={filteredData?.length} pagingation={false} />
      </div>
      <Modal trigger='click' centered open={open} width={window.innerWidth < 768 ? 350 : 400} footer={null} destroyOnClose onCancel={handleOpen}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            {!isEdit ? 'Create User' : 'Update User'}
          </div>
          <CreateUser handleOpen={handleOpen} refetch={refetch} userData={userModalData} userList={localData} isEdit={isEdit} />
        </div>
      </Modal>
    </div>
  );
};
